import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
  Alert,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label
} from 'reactstrap';
import axios from '../../api/Axios';

const TutorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [tutor, setTutor] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [authorizationUpdating, setAuthorizationUpdating] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Verification modal state
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [docToVerify, setDocToVerify] = useState(null);
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [liabilityChecked, setLiabilityChecked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  // Rejection modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [docToReject, setDocToReject] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Fraudulent modal state
  const [fraudModalOpen, setFraudModalOpen] = useState(false);
  const [docToMarkFraud, setDocToMarkFraud] = useState(null);
  const [fraudProcessing, setFraudProcessing] = useState(false);

  // Verify user modal state
  const [verifyUserModalOpen, setVerifyUserModalOpen] = useState(false);
  const [verifyingUser, setVerifyingUser] = useState(false);

  useEffect(() => {
    fetchTutorDetails();
  }, [id]);

  useEffect(() => {
    if (tutor) {
      fetchTutorDocuments();
      fetchTutorBranches();
    }
  }, [tutor]);

  const fetchTutorDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/accounts/tutors/${id}/`);
      setTutor(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutor details:', err);
      setError('Failed to load tutor details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTutorDocuments = async () => {
    try {
      if (!tutor || !tutor.user_id) return;
      
      const response = await axios.get(`/api/accounts/tutor-docs/?user=${tutor.user_id}`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setDocuments([]);
    }
  };

  const fetchTutorBranches = async () => {
    try {
      if (!tutor || !tutor.user_id) return;
      
      setBranchesLoading(true);
      const response = await axios.get(`/api/accounts/tutors/${tutor.user_id}/branches/`);
      setBranches(response.data);
    } catch (err) {
      console.error('Error fetching tutor branches:', err);
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleAuthorizationChange = async (branch, level, isChecked) => {
    const updateKey = `${branch.id}-${level}`;
    setAuthorizationUpdating(prev => ({ ...prev, [updateKey]: true }));

    try {
      // Get current authorized levels
      const currentAuthorizedLevels = branch.authorized_levels || [];
      
      // Add or remove the level based on checkbox state
      const newAuthorizedLevels = isChecked 
        ? [...currentAuthorizedLevels, level]
        : currentAuthorizedLevels.filter(l => l !== level);

      // Call API to update authorization
      const response = await axios.patch(`/api/accounts/tutor-branches/${branch.id}/authorize/`, {
        authorized_levels: newAuthorizedLevels
      });

      console.log('Authorization update response:', response.data);

      // Update local state
      setBranches(prev => prev.map(b => 
        b.id === branch.id 
          ? { ...b, authorized_levels: newAuthorizedLevels }
          : b
      ));

    } catch (err) {
      console.error('Error updating authorization:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      alert(`Failed to update authorization: ${err.response?.data?.error || err.message}`);
    } finally {
      setAuthorizationUpdating(prev => {
        const updated = { ...prev };
        delete updated[updateKey];
        return updated;
      });
    }
  };

  const getStatusBadge = (regStatus) => {
    const statusMap = {
      0: { color: 'warning', text: 'Pending' },
      1: { color: 'info', text: 'Step 1 Complete' },
      2: { color: 'success', text: 'Verified' },
      3: { color: 'primary', text: 'Step 3 Complete' },
      4: { color: 'success', text: 'Step 4 Complete' },
      5: { color: 'success', text: 'Fully Registered' }
    };
    const status = statusMap[regStatus] || { color: 'secondary', text: 'Unknown' };
    return <Badge color={status.color}>{status.text}</Badge>;
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <Badge color="success">
        <i className="bi bi-check-circle me-1"></i>
        Verified
      </Badge>
    ) : (
      <Badge color="warning">
        <i className="bi bi-clock me-1"></i>
        Pending
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getQualificationLevel = (qualificationId) => {
    if (!tutor || !tutor.qualifications) return null;
    const qual = tutor.qualifications.find(q => q.id === qualificationId);
    return qual ? qual.qualification_level : null;
  };

  const getQualificationDocuments = () => {
    return documents.filter(doc => doc.qualification);
  };

  const getGeneralDocuments = () => {
    return documents.filter(doc => !doc.qualification);
  };

  // Verification modal functions
  const openVerifyModal = (doc) => {
    setDocToVerify(doc);
    setVerificationChecked(false);
    setLiabilityChecked(false);
    setVerifyModalOpen(true);
  };

  const closeVerifyModal = () => {
    setVerifyModalOpen(false);
    setDocToVerify(null);
    setVerificationChecked(false);
    setLiabilityChecked(false);
  };

  const handleVerifyDocument = async () => {
    if (!verificationChecked || !liabilityChecked) {
      alert('Both acknowledgements are required to verify this document.');
      return;
    }

    setVerifying(true);
    try {
      await axios.patch(`/api/accounts/tutor-docs/${docToVerify.id}/update-status/`, {
        doc_status: 'Verified'
      });

      // Update local state
      setDocuments(prev => prev.map(doc => 
        doc.id === docToVerify.id ? { ...doc, doc_status: 'Verified' } : doc
      ));

      // Refresh tutor details to update qualification status
      await fetchTutorDetails();

      closeVerifyModal();
      alert('Document verified successfully!');
    } catch (err) {
      console.error('Error verifying document:', err);
      alert('Failed to verify document. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Rejection modal functions
  const openRejectModal = (doc) => {
    setDocToReject(doc);
    setRejectComment('');
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setDocToReject(null);
    setRejectComment('');
  };

  const handleRejectDocument = async () => {
    if (!rejectComment.trim()) {
      alert('Please provide a reason for rejecting this document.');
      return;
    }

    setRejecting(true);
    try {
      await axios.patch(`/api/accounts/tutor-docs/${docToReject.id}/update-status/`, {
        doc_status: 'Rejected',
        comments: rejectComment
      });

      // Update local state
      setDocuments(prev => prev.map(doc => 
        doc.id === docToReject.id ? { ...doc, doc_status: 'Rejected', comments: rejectComment } : doc
      ));

      // Refresh tutor details to update reg_status
      await fetchTutorDetails();

      closeRejectModal();
      alert('Document rejected successfully.');
    } catch (err) {
      console.error('Error rejecting document:', err);
      alert('Failed to reject document. Please try again.');
    } finally {
      setRejecting(false);
    }
  };

  // Fraudulent modal functions
  const openFraudModal = (doc) => {
    setDocToMarkFraud(doc);
    setFraudModalOpen(true);
  };

  const closeFraudModal = () => {
    setFraudModalOpen(false);
    setDocToMarkFraud(null);
  };

  const handleMarkFraudulent = async () => {
    setFraudProcessing(true);
    try {
      await axios.patch(`/api/accounts/tutor-docs/${docToMarkFraud.id}/update-status/`, {
        doc_status: 'Fraudulent'
      });

      // Update local state
      setDocuments(prev => prev.map(doc => 
        doc.id === docToMarkFraud.id ? { ...doc, doc_status: 'Fraudulent' } : doc
      ));

      // Refresh tutor details as user will be deactivated
      await fetchTutorDetails();

      closeFraudModal();
      alert('Document marked as fraudulent and user account has been deactivated.');
    } catch (err) {
      console.error('Error marking document as fraudulent:', err);
      alert('Failed to mark document as fraudulent. Please try again.');
    } finally {
      setFraudProcessing(false);
    }
  };

  // Verify user modal functions
  const openVerifyUserModal = () => {
    setVerifyUserModalOpen(true);
  };

  const closeVerifyUserModal = () => {
    setVerifyUserModalOpen(false);
  };

  const handleVerifyUser = async () => {
    setVerifyingUser(true);
    try {
      await axios.post(`/accounts/tutors/${tutor.user_id}/update-reg-status/`, {
        reg_status: 2
      });

      // Refresh tutor details to show updated status
      await fetchTutorDetails();

      closeVerifyUserModal();
      alert('Tutor has been successfully verified and can now provide services!');
    } catch (err) {
      console.error('Error verifying tutor:', err);
      alert('Failed to verify tutor. Please try again.');
    } finally {
      setVerifyingUser(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading tutor details...</p>
      </div>
    );
  }

  if (error || !tutor) {
    return (
      <div className="container-fluid">
        <Alert color="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || 'Tutor not found'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button 
            color="link" 
            className="ps-0 mb-2"
            onClick={() => navigate('/tutor-provider-dashboard/tutors')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Tutors
          </Button>
          <h2 className="mb-0">
            <i className="bi bi-person-badge me-2"></i>
            {tutor.title || ''} {tutor.other_names} {tutor.surname}
          </h2>
          <div className="mt-2">
            {getStatusBadge(tutor.reg_status)}
            {tutor.provider_code && (
              <code className="text-muted ms-2">Provider: {tutor.provider_code}</code>
            )}
          </div>
        </div>
      </div>

      <Row>
        <Col md="8">
          {/* Basic Information */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-person-lines-fill me-2"></i>
                Basic Information
              </CardTitle>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>Gender</th>
                    <td>{tutor.gender || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Citizenship</th>
                    <td>{tutor.citizenship || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>ID Type</th>
                    <td>{tutor.id_document_type || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>ID Number</th>
                    <td>{tutor.id_number || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>
                      <a href={`mailto:${tutor.email}`}>{tutor.email || 'N/A'}</a>
                    </td>
                  </tr>
                  <tr>
                    <th>Phone</th>
                    <td>{tutor.phone_number || 'N/A'}</td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Documents Tables */}
          {getQualificationDocuments().length > 0 && (
            <Card className="mb-4">
              <CardBody>
                <CardTitle tag="h5">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Qualification Documents
                  <Badge color="success" pill className="ms-2">
                    {getQualificationDocuments().length}
                  </Badge>
                </CardTitle>
                <Table size="sm" hover responsive>
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Document Type</th>
                      <th>Status</th>
                      <th>Uploaded</th>
                      <th>Download</th>
                      <th>Accept</th>
                      <th>Reject</th>
                      <th>Fraudulent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getQualificationDocuments().map((doc, index) => (
                      <tr key={index}>
                        <td>
                          <Badge color="primary" pill>
                            {getQualificationLevel(doc.qualification) || 'N/A'}
                          </Badge>
                        </td>
                        <td>{doc.doc_type}</td>
                        <td>
                          <Badge color={
                            doc.doc_status === 'Verified' ? 'success' : 
                            doc.doc_status === 'Rejected' ? 'danger' : 
                            doc.doc_status === 'Fraudulent' ? 'dark' : 'warning'
                          }>
                            {doc.doc_status}
                          </Badge>
                        </td>
                        <td>{formatDate(doc.uploaded_at)}</td>
                        <td>
                          <Button 
                            color="primary" 
                            size="sm"
                            onClick={() => window.open(doc.document_url || doc.document, '_blank')}
                            disabled={!doc.document}
                          >
                            <i className="bi bi-download"></i>
                          </Button>
                        </td>
                        <td>
                          <Button 
                            color="success" 
                            size="sm"
                            onClick={() => openVerifyModal(doc)}
                            disabled={doc.doc_status !== 'Pending'}
                          >
                            <i className="bi bi-check-circle"></i>
                          </Button>
                        </td>
                        <td>
                          <Button 
                            color="danger" 
                            size="sm"
                            onClick={() => openRejectModal(doc)}
                            disabled={doc.doc_status !== 'Pending'}
                          >
                            <i className="bi bi-x-circle"></i>
                          </Button>
                        </td>
                        <td>
                          <Button
                            color="dark"
                            size="sm"
                            onClick={() => openFraudModal(doc)}
                            disabled={doc.doc_status !== 'Pending'}
                          >
                            <i className="bi bi-exclamation-octagon"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {/* General Documents Section */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-file-earmark-person me-2"></i>
                General Documents (ID, Proof of Address, etc.)
                {getGeneralDocuments().length > 0 && (
                  <Badge color="info" pill className="ms-2">
                    {getGeneralDocuments().length}
                  </Badge>
                )}
              </CardTitle>
              {getGeneralDocuments().length === 0 ? (
                <Alert color="info" className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  No general documents uploaded yet
                </Alert>
              ) : (
                <Table size="sm" hover responsive>
                  <thead>
                    <tr>
                      <th>Document Type</th>
                      <th>Status</th>
                      <th>Uploaded</th>
                      <th>Download</th>
                      <th>Accept</th>
                      <th>Reject</th>
                      <th>Fraudulent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getGeneralDocuments().map((doc, index) => (
                      <tr key={index}>
                        <td>{doc.doc_type}</td>
                        <td>
                          <Badge color={
                            doc.doc_status === 'Verified' ? 'success' : 
                            doc.doc_status === 'Rejected' ? 'danger' : 
                            doc.doc_status === 'Fraudulent' ? 'dark' : 'warning'
                          }>
                            {doc.doc_status}
                          </Badge>
                        </td>
                        <td>{formatDate(doc.uploaded_at)}</td>
                        <td>
                          <Button 
                            color="primary" 
                            size="sm"
                            onClick={() => window.open(doc.document_url || doc.document, '_blank')}
                            disabled={!doc.document}
                          >
                            <i className="bi bi-download"></i>
                          </Button>
                        </td>
                        <td>
                          <Button 
                            color="success" 
                            size="sm"
                            onClick={() => openVerifyModal(doc)}
                            disabled={doc.doc_status !== 'Pending'}
                          >
                            <i className="bi bi-check-circle"></i>
                          </Button>
                        </td>
                        <td>
                          <Button 
                            color="danger" 
                            size="sm"
                            onClick={() => openRejectModal(doc)}
                            disabled={doc.doc_status !== 'Pending'}
                          >
                            <i className="bi bi-x-circle"></i>
                          </Button>
                        </td>
                        <td>
                          <Button
                            color="dark"
                            size="sm"
                            onClick={() => openFraudModal(doc)}
                            disabled={doc.doc_status !== 'Pending'}
                          >
                            <i className="bi bi-exclamation-octagon"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>

          {/* Subjects & Branches Section */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-journals me-2"></i>
                Subject Areas & Teaching Levels
                {branches.length > 0 && (
                  <Badge color="primary" pill className="ms-2">
                    {branches.length}
                  </Badge>
                )}
              </CardTitle>
              
              {branchesLoading ? (
                <div className="text-center py-3">
                  <Spinner color="primary" size="sm" className="me-2" />
                  Loading subjects...
                </div>
              ) : branches.length === 0 ? (
                <Alert color="info" className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  No subject areas selected yet
                </Alert>
              ) : (
                <div>
                  {/* Group branches by subject */}
                  {Object.entries(
                    branches.reduce((acc, branch) => {
                      const subjectName = branch.branch_details.subject_name;
                      const subjectCategory = branch.branch_details.subject_category;
                      const subjectKey = `${subjectName} (${subjectCategory})`;
                      
                      if (!acc[subjectKey]) {
                        acc[subjectKey] = [];
                      }
                      acc[subjectKey].push(branch);
                      return acc;
                    }, {})
                  ).map(([subjectName, subjectBranches]) => (
                    <div key={subjectName} className="mb-4">
                      <h6 className="border-bottom pb-2 mb-3">
                        <Badge 
                          color={subjectBranches[0]?.branch_details?.subject_category === 'STEM' ? 'success' : 'info'} 
                          className="me-2"
                        >
                          {subjectBranches[0]?.branch_details?.subject_category}
                        </Badge>
                        {subjectName.replace(` (${subjectBranches[0]?.branch_details?.subject_category})`, '')}
                      </h6>
                      
                      <Table size="sm" className="mb-3">
                        <thead>
                          <tr>
                            <th>Branch/Specialization</th>
                            <th>Teaching Levels & Authorization</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subjectBranches.map((branch) => (
                            <tr key={branch.id}>
                              <td>
                                <strong>{branch.branch_details.name}</strong>
                              </td>
                              <td>
                                {branch.teaching_levels && branch.teaching_levels.length > 0 ? (
                                  <div>
                                    {branch.teaching_levels.map((level, index) => {
                                      const isAuthorized = branch.authorized_levels && branch.authorized_levels.includes(level);
                                      const isUpdating = authorizationUpdating[`${branch.id}-${level}`];
                                      
                                      return (
                                        <div key={index} className="d-flex align-items-center mb-2">
                                          <input
                                            type="checkbox"
                                            className="form-check-input me-2"
                                            checked={isAuthorized}
                                            onChange={(e) => handleAuthorizationChange(branch, level, e.target.checked)}
                                            disabled={isUpdating}
                                          />
                                          <Badge 
                                            color={isAuthorized ? "success" : "secondary"} 
                                            outline={!isAuthorized}
                                            className="me-1 position-relative"
                                            title={`${isAuthorized ? 'Authorized' : 'Not authorized'} to teach at ${level} level`}
                                          >
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                            {isUpdating && (
                                              <Spinner size="sm" className="position-absolute top-0 start-100 translate-middle" />
                                            )}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-muted">No levels specified</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col md="4">
          {/* Verification Status */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-shield-check me-2"></i>
                Verification Status
              </CardTitle>
              <Table borderless size="sm">
                <tbody>
                  <tr>
                    <td><strong>Registration Status</strong></td>
                    <td className="text-end">{getStatusBadge(tutor.reg_status)}</td>
                  </tr>
                  <tr>
                    <td><strong>Email Verified</strong></td>
                    <td className="text-end">{getVerificationBadge(tutor.email_verified)}</td>
                  </tr>
                  <tr>
                    <td><strong>Documents Verified</strong></td>
                    <td className="text-end">{getVerificationBadge(tutor.document_verified)}</td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-lightning me-2"></i>
                Quick Actions
              </CardTitle>
              <div className="d-grid gap-2">
                <Button 
                  color="outline-info" 
                  size="sm"
                  onClick={() => window.open(`mailto:${tutor.email}`)}
                >
                  <i className="bi bi-envelope me-2"></i>
                  Send Email
                </Button>
                <Button 
                  color="outline-success" 
                  size="sm"
                  onClick={openVerifyUserModal}
                  disabled={tutor.reg_status === 2}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Verify User
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Verification Modal */}
      <Modal isOpen={verifyModalOpen} toggle={closeVerifyModal}>
        <ModalHeader toggle={closeVerifyModal}>
          <i className="bi bi-shield-check me-2"></i>
          Verify Document
        </ModalHeader>
        <ModalBody>
          {docToVerify && (
            <div>
              <Alert color="info">
                <strong>Document:</strong> {docToVerify.doc_type}
                {docToVerify.qualification && (
                  <div>
                    <strong>Level:</strong>{' '}
                    <Badge color="primary" pill>
                      {getQualificationLevel(docToVerify.qualification) || 'N/A'}
                    </Badge>
                  </div>
                )}
              </Alert>
              
              <p className="mb-3">
                Please confirm the following before verifying this document:
              </p>
              
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="verificationCheck"
                  checked={verificationChecked}
                  onChange={(e) => setVerificationChecked(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="verificationCheck">
                  I have verified this qualification directly with the institution via an official channel (e.g., phone, website, or WhatsApp).
                </label>
              </div>
              
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="liabilityCheck"
                  checked={liabilityChecked}
                  onChange={(e) => setLiabilityChecked(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="liabilityCheck">
                  I am aware that I can be held responsible personally should this qualification be fraudulent.
                </label>
              </div>
              
              {(!verificationChecked || !liabilityChecked) && (
                <Alert color="warning" className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Both acknowledgements are required to verify this document.
                </Alert>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color="success" 
            onClick={handleVerifyDocument}
            disabled={!verificationChecked || !liabilityChecked || verifying}
          >
            {verifying ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verifying...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Verify Document
              </>
            )}
          </Button>
          <Button color="secondary" onClick={closeVerifyModal} disabled={verifying}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Rejection Modal */}
      <Modal isOpen={rejectModalOpen} toggle={closeRejectModal}>
        <ModalHeader toggle={closeRejectModal}>
          <i className="bi bi-x-circle me-2"></i>
          Reject Document
        </ModalHeader>
        <ModalBody>
          {docToReject && (
            <div>
              <Alert color="warning">
                <strong>Document:</strong> {docToReject.doc_type}
                {docToReject.qualification && (
                  <div>
                    <strong>Level:</strong>{' '}
                    <Badge color="primary" pill>
                      {getQualificationLevel(docToReject.qualification) || 'N/A'}
                    </Badge>
                  </div>
                )}
              </Alert>
              
              <div className="mb-3">
                <Label for="rejectComment">
                  <strong>Rejection Reason <span className="text-danger">*</span></strong>
                </Label>
                <Input
                  type="textarea"
                  id="rejectComment"
                  rows="4"
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                />
              </div>
              
              {!rejectComment.trim() && (
                <Alert color="danger" className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  A rejection reason is required.
                </Alert>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color="danger" 
            onClick={handleRejectDocument}
            disabled={!rejectComment.trim() || rejecting}
          >
            {rejecting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Rejecting...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-2"></i>
                Reject Document
              </>
            )}
          </Button>
          <Button color="secondary" onClick={closeRejectModal} disabled={rejecting}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Fraudulent Modal */}
      <Modal isOpen={fraudModalOpen} toggle={closeFraudModal}>
        <ModalHeader toggle={closeFraudModal}>
          <i className="bi bi-exclamation-octagon me-2 text-danger"></i>
          Mark as Fraudulent
        </ModalHeader>
        <ModalBody>
          {docToMarkFraud && (
            <div>
              <Alert color="info">
                <strong>Document:</strong> {docToMarkFraud.doc_type}
                {docToMarkFraud.qualification && (
                  <>
                    <br />
                    <strong>Level:</strong>{' '}
                    <Badge color="primary" pill>
                      {getQualificationLevel(docToMarkFraud.qualification) || 'N/A'}
                    </Badge>
                  </>
                )}
              </Alert>
              
              <div className="bg-light p-3 border-start border-4 border-warning">
                <p className="mb-0">
                  <strong>⚠️ Only proceed if you are absolutely certain this submission is fraudulent.</strong>
                </p>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color="danger" 
            onClick={handleMarkFraudulent}
            disabled={fraudProcessing}
          >
            {fraudProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="bi bi-exclamation-octagon me-2"></i>
                Mark as Fraudulent
              </>
            )}
          </Button>
          <Button color="secondary" onClick={closeFraudModal} disabled={fraudProcessing}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Verify User Modal */}
      <Modal isOpen={verifyUserModalOpen} toggle={closeVerifyUserModal}>
        <ModalHeader toggle={closeVerifyUserModal}>
          <i className="bi bi-shield-check me-2 text-success"></i>
          Verify Tutor
        </ModalHeader>
        <ModalBody>
          <div>
            <Alert color="success">
              <strong>Tutor:</strong> {tutor.title || ''} {tutor.other_names} {tutor.surname}
              <br />
              <strong>Email:</strong> {tutor.email}
            </Alert>
            
            <div className="bg-light p-3 border-start border-4 border-success">
              <p className="mb-3">
                <strong>✅ This tutor will be marked as verified and will be able to start providing services.</strong>
              </p>
              <p className="mb-0 text-muted">
                Once verified, this tutor will have full access to provide tutoring services through the platform.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="success" 
            onClick={handleVerifyUser}
            disabled={verifyingUser}
          >
            {verifyingUser ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verifying...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Verify Tutor
              </>
            )}
          </Button>
          <Button color="secondary" onClick={closeVerifyUserModal} disabled={verifyingUser}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TutorDetails;